import React, {useEffect, useState} from 'react';
import {IonContent, IonModal, IonPage} from '@ionic/react';
import {useHistory, Link} from 'react-router-dom';
import RoadTest from './RoadTest';
import DailyReport from './DailyReport';
import ReportHistory from './ReportHistory';
import {ApiService} from "../api/ApiService";
import {toastStore} from "../components/AppToast";
import {userStore} from "../api/AuthService";
import {Trainee} from "../api/model/Trainee";
import {TrainingCourse} from "../api/model/TrainingCourses";
import LoadDetailHeader, {useTransparentHeaderContrast} from '../components/LoadDetailHeader';
import {
  AccidentsIcon,
  BookIcon,
  ChevronLeft,
  ChevronRight,
  ClimateIcon,
  CloseIcon,
  ExpressLaneIcon,
  InfoIcon,
  PaperworkIcon,
  PlusIcon,
  ReleaseIcon,
  SafetyIcon,
  SeatedIcon,
  TrailerIcon,
  VehicleIcon,
  WalkthroughIcon,
} from '../components/icons';

interface Course {
    enabled: number;
    id: number;
    title: string;
    total_backing_events: number;
    total_days: number;
}

type ScoreKey = 'backing' | 'turning' | 'roadHabits' | 'city' | 'highway' | 'tablet' | 'hos' | 'defensive';

const SCORE_SECTIONS: Array<{
    key: ScoreKey;
    title: string;
    icon: React.ReactNode;
    intro: string;
    bullets: string[];
    levels: string[];
}> = [
    {
        key: 'backing',
        title: 'Backing',
        icon: <TrailerIcon />,
        intro: 'Rate Trainee on their backing performance based on the following.',
        bullets: ['GOAL: Get Out And Look', 'Blows horn before starting the back', 'Mirror Use', '45, 90 and Straight Back'],
        levels: [
            'Trainee is unable to complete the movement successfully. Cannot perform a 45, 90, or straight back without step-by-step instruction from the trainer.',
            'Trainee can perform a backing event; however, not in a timely manner. Takes the trainee more than 10 minutes with multiple pull ups. Trainee does not do one or any of the following: GOAL (Get Out and Look), use mirrors, honk horn before backing.',
            'Trainee can perform a backing event in under 10 minutes with multiple pull ups. Trainee does not do one of the following: GOAL (Get Out and Look), use mirrors, honk horn before backing.',
            'Trainee can perform a backing event in under 7 minutes with 5 or less pull ups. Trainee does all of the following: GOAL (Get Out and Look), use mirrors, honk horn before backing.',
            'Trainee can perform a backing event in under 5 minutes with 3 or less pull ups. Trainee does all of the following: GOAL (Get Out and Look), use mirrors, honk horn before backing.',
        ],
    },
    {
        key: 'turning',
        title: 'Turning',
        icon: <VehicleIcon />,
        intro: 'Rate Trainee on their turning performance based on the following.',
        bullets: ['Set up and execution of a turn', 'Uses mirrors to clear trailer tires'],
        levels: [
            'Trainee is unable to negotiate a turn without step-by-step instruction from the trainer. Trainee may have a close call during a turning event where tires come up on the curb or into the shoulder of the road. Trainee does not set up the turn properly or use mirrors to clear the trailer tires.',
            'Trainee can negotiate a turn, however; does not set up properly and may be curbing their tires. Also not turning into the proper lane at an intersection.',
            'Trainee can negotiate a turn, however; one of the following is an issue: does not set up properly, curbs tire or runs onto sidewalk, or is turning into an incorrect lane at an intersection.',
            'Trainee can negotiate a turn. Can set up properly, uses mirrors to clear trailer tires, and is turning into the proper lane; however, may have occasional issues when performing multiple turns in a day.',
            'Trainee can successfully negotiate a turn consistently. Has a proper set up, uses mirrors to clear trailer tires, and turns into the proper lane. Consistent performance throughout the day.',
        ],
    },
    {
        key: 'roadHabits',
        title: 'Driving Habits',
        icon: <ClimateIcon />,
        intro: 'Rate Trainee on their driving habits for various road conditions based on the following.',
        bullets: ['Ramps and Curves', 'Downhill', 'Weather Conditions', 'Traffic Conditions'],
        levels: [
            'Trainee does not adjust driving habits for road or weather conditions. Driving too fast for conditions, does not control speed downhill or around ramps or curves, and is not adjusting to traffic conditions.',
            'Trainee is not consistent in adjusting driving habits for changing conditions throughout the day. May adjust occasionally, but not for all necessary changes is conditions.',
            'Trainee recognizes conditions where driving habits need to be adjusted but does not fully adjust driving habits. Example: trainee slows down but does not also increase following distance in inclement weather.',
            'Trainee recognizes and adjusts driving habits for conditions, however; may need to improve in one of the following areas (ramps and curves, controlling speed downhill, weather conditions, traffic conditions).',
            'Trainee always adjusts driving habits according to conditions. Trainee effectively recognizes and changes driving behavior for changes in the following ramps and curves, downhill, weather conditions, and traffic conditions.',
        ],
    },
    {
        key: 'city',
        title: 'City Driving',
        icon: <VehicleIcon />,
        intro: 'Rate Trainee on their city driving based on the following.',
        bullets: ['Turns', 'Signaling', 'Mirror Use'],
        levels: [
            'Trainee does not negotiate turns, intersections, does not signal, or use their mirrors to negotiate traffic. Trainee may have a near miss while in a city or industrial area.',
            'Trainee can drive in cities and industrial areas; however, does not negotiate intersections or turns properly, does not signal, and only occasionally uses their mirrors in changing lanes. Trainee may be waiting until the last chance to change lanes for an exit or get into the proper lane for an intersection.',
            'Trainee can drive in cities and industrial areas; however, needs to improve on more than one of the following areas: negotiating intersections and turns, signaling appropriately, or using their mirrors when changing lanes or negotiating turns.',
            'Trainee can drive in cities and industrial areas proficiently; however, needs to improve on one of the following areas: negotiating intersections and turns, signaling appropriately, or using their mirrors when changing lanes or negotiating turns.',
            'Trainee proficiently negotiates city and industrial areas. Trainee negotiates intersections and turns properly, signals in advance of movement, and always uses their mirrors when changing lanes or negotiating turns.',
        ],
    },
    {
        key: 'highway',
        title: 'Highway Driving',
        icon: <ExpressLaneIcon />,
        intro: 'Rate Trainee on their highway / interstate driving based on the following.',
        bullets: ['Lane Control and Lane usage', 'Appropriate Speed Control', 'Sitting Duck: Never stop on the side of the road unless you have experienced a true emergency'],
        levels: [
            'Trainee does not maintain their lane, is in a restricted lane, and does not control their speed or adjust speed for construction zones and changing speed zones. Any time the trainee stops on the side of the road for something other than an emergency it is an automatic 1 in this section.',
            'Trainee can drive on the highway, however; is not proficient in more than one of the following areas: maintaining lane control, using the appropriate lane, maintaining an appropriate speed, or adjusting speed for construction zones or changing speed limits.',
            'Trainee can drive on the highway; however, is not proficient in only one of the following areas: maintaining lane control, using the appropriate lane, maintaining an appropriate speed, or adjusting speed for construction zones or changing speed limits.',
            'Trainee can drive on the highway, however; may have an occasional critical event or one event throughout the day of the following: maintaining lane control, using the appropriate lane, maintaining an appropriate speed, or adjusting speed for construction zones or changing speed limits.',
            'Trainee shows proficiency in all areas of highway driving. Trainee maintains lane control and is always in the appropriate lane, always maintains an appropriate speed on the highway, and adjusts their speed appropriately for construction zones and changes in the speed limit.',
        ],
    },
    {
        key: 'tablet',
        title: 'Tablet',
        icon: <PaperworkIcon />,
        intro: 'Rate Trainee on their tablet usage based on the following.',
        bullets: ['GPS', 'Workflow', 'Loaded, Empty, Messaging', 'DVIR / Pretrip', 'HOS App'],
        levels: [
            'Trainee does not understand how to use the tablet and requires direct supervision on all tasks using the tablet.',
            'Trainee can use the tablet but does require assistance regularly. Trainee is not proficient in more than one of the following areas: GPS / Navigation, Workflow, messages (including loaded or empty), DVIR or Pretrip Inspection submissions, and the HOS App.',
            'Trainee can use the tablet but requires assistance occasionally. Trainee is not proficient in only one of the following areas: GPS / Navigation, Workflow, messages (including loaded or empty), DVIR or Pretrip Inspection submissions, and the HOS App.',
            'Trainee can use the tablet but may still require assistance. Trainee shows proficiency in each of the following apps: GPS / Navigation, Workflow, messages (including loaded or empty), DVIR or Pretrip Inspection submissions, and the HOS App.',
            'Trainee shows proficiency in using the tablet. The trainee requires no assistance and is proficient in all the following apps: GPS / Navigation, Workflow, messages (including loaded or empty), DVIR or Pretrip Inspection submissions, and the HOS App.',
        ],
    },
    {
        key: 'hos',
        title: 'Service Hours',
        icon: <BookIcon />,
        intro: 'Rate Trainee on their hours of service knowledge based on the following.',
        bullets: ['Understands trip planning and HOS Rules and Regulations'],
        levels: [
            'Trainee does not understand hours of service or trip planning. Is unable to plan out their day or utilize their available time.',
            'Trainee needs assistance regularly in understanding available time. Has a general understanding of the hours of service but still does not fully understand the regulation. Trainee cannot plan out their day according to available hours.',
            'Trainee understands available hours and hours of service regulations. Trainee does need assistance in trip planning to understand how to utilize available hours and take breaks on time.',
            'Trainee understands hours of service and available time. Trainee can plan their trip, but may need assistance routing.',
            'Trainee shows proficiency in understanding the hours of service regulations and effectively plans their trip for their available time. Trip plan includes the following areas: routing, scaling their load, potential weigh stations, break locations with back up locations, and on time ETA for delivery.',
        ],
    },
    {
        key: 'defensive',
        title: 'Defensive Driving',
        icon: <SafetyIcon />,
        intro: 'Rate Trainee on their defensive driving based on the following.',
        bullets: ['Aim high in steering', 'Get the big picture', 'Keep your eyes moving', 'Leave yourself an out', 'Make sure they see you'],
        levels: [
            'Trainee does not understand defensive driving principles and does not practice the 5 Key Points of Safe Driving.',
            'Trainee requires monitoring and does not do more than one of the following: aim high in steering, get the big picture, keep your eyes moving, leave yourself an out, and make sure they see you.',
            'Trainee understands the 5 Key Points of Safe Driving; however, needs improvement in one of the following areas: aim high in steering, get the big picture, keep your eyes moving, leave yourself an out, and make sure they see you.',
            'Trainee understands and practices the 5 Key Points of Safe Driving. Trainee may still have one critical event or rare event in the day regarding Defensive Driving practices.',
            'Trainee shows proficiency in Defensive Driving. Understands the 5 Key Points of Safe Driving, and is consistently practicing these key points.',
        ],
    },
];

const TrainingDay: React.FC = () => {
    const history = useHistory();
    const {onSheet, onIonScroll} = useTransparentHeaderContrast();
    const now = new Date()

    const [mounted, setMounted] = useState(false);
    const [showNewReport, setShowNewReport] = useState(false)
    const [traineeData, setTraineeData] = useState<Trainee>()

    const [showModal, setShowModal] = useState(false);
    const [showModal1, setShowModal1] = useState(false);
    const [showModal2, setShowModal2] = useState(false);
    const [showModal3, setShowModal3] = useState(false);

    const [showRoadTest, setShowRoadTest] = useState(false);
    const [reportId, setReportId] = useState(0);
    const [traineeCode] = useState('');
    const [courses, setCourses] = useState<Course[]>([]);
    const [reportTimer] = useState<string>('Not Ready');
    const [showCourseModal, setShowCourseModal] = useState(false);
    const [hasTrainee, setHasTrainee] = useState(false);
    const [submittedTest, setSubmittedTest] = useState(0);
    const [noTrainees, setNoTrainees] = useState(false);
    const [trainees, setTrainees] = useState<Trainee[]>([]);
    const [course, setCourse] = useState<Course>({
        enabled: 0,
        id: 0,
        title: '',
        total_backing_events: 0,
        total_days: 0
    });
    const [changingCourse, setChangingCourse] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState<number>();
    const [traineeAccident, setTraineeAccident] = useState('');
    const [traineeReady, setTraineeReady] = useState('');
    const [trainee] = useState<Trainee>({
        currentCourseDay: 0,
        traineeCode: '',
        traineeName: '',
        currentCourseId: 0,
        dateCreated: '',
        dmCode: '',
        passed: 0,
        trainerCode: '',
        trainerName: '',
        released: 0,
        seatedDate: ''
    });

    const [scores, setScores] = useState<Record<ScoreKey, number>>({
        backing: 0,
        turning: 0,
        roadHabits: 0,
        city: 0,
        highway: 0,
        tablet: 0,
        hos: 0,
        defensive: 0,
    });
    const [openSection, setOpenSection] = useState<ScoreKey | null>(null);

    useEffect(() => {
      const id = requestAnimationFrame(() => {
        setTimeout(() => setMounted(true), 30);
      });
      return () => cancelAnimationFrame(id);
    }, []);

    useEffect(() => {
        ApiService.getTrainees('trainingDay', {}).then(it => {
            setNoTrainees(it.length === 0);
            setTrainees(it.filter(trainee => trainee.passed === null));
        });
    }, []);

    const getCourseInfo = () => {
        ApiService.getTrainingCourses("trainingDay").then(it => {
            parseCourses(it);
        });
    }

    const parseCourses = (courseArray: TrainingCourse[]) => {
        let tempArray: Course[] = [];
        courseArray.forEach((element: any) => {
            if (element.enabled === 1) {
                tempArray.push(element);
            }
            if (element.id === trainee.currentCourseId) {
                setCourse(element);
            } else {
                setCourse({enabled: 0, id: 0, title: "No course assigned", total_backing_events: 0, total_days: 0});
            }
        });
        setCourses(tempArray);
    }

    const handleSubmit = () => {
        if (!scoresCheck() && !traineeAccident) {
            toastStore.getState().setToast('Please fill out the report completely');
        } else {
            submitReport();
        }
    }

    const scoresCheck = () => {
        return !(scores.backing === 0 ||
            scores.turning === 0 ||
            scores.tablet === 0 ||
            scores.roadHabits === 0 ||
            scores.hos === 0 ||
            scores.highway === 0 ||
            scores.city === 0 ||
            scores.defensive === 0 ||
            traineeAccident === '' ||
            traineeReady === '');
    }

    const submitCourseChange = () => {
        ApiService.assignTraineeCourse("trainingDay", {traineeCode: trainee.traineeCode, courseId: selectedCourseId!}).then(() => {
            setShowModal2(true);
            setShowModal3(false);
        });
    }

    const submitReport = async () => {
        ApiService.submitTrainingReport2("trainingDay", {
            dmCode: userStore.getState().driver?.dmCode || trainee.dmCode!,
            traineeDrove: 1,
            traineeCode: trainee.traineeCode,
            courseId: trainee.currentCourseId,
            courseDay: trainee.currentCourseDay,
            reportData: JSON.stringify({
                backingScore: scores.backing,
                turningScore: scores.turning,
                tabletScore: scores.tablet,
                roadHabitsScore: scores.roadHabits,
                hosScore: scores.hos,
                highwayScore: scores.highway,
                cityScore: scores.city,
                defensiveScore: scores.defensive
            }),
            overallScore: scores.backing + scores.turning + scores.tablet + scores.roadHabits + scores.hos + scores.highway + scores.city + scores.defensive,
            traineeAccident: traineeAccident ? 1 : 0,
            traineeReady: traineeReady ? 1 : 0,
            roadTest: submittedTest,
            reportId: reportId
        }).then(() => {
            toastStore.getState().setSuccessToast('Your report has been submitted!');
            setShowModal(true);
        }).catch(() => {
            toastStore.getState().setToast('Your report could not be submitted. Please check your connection and try again.');
        }).finally(() => {
            setShowModal1(false);
        });
    }

    const testSubmitted = () => {
        setSubmittedTest(1);
    }

    const resetForm = () => {
        setShowRoadTest(false);
    }

    const resetScores = () => {
        setScores({
            backing: 0,
            turning: 0,
            roadHabits: 0,
            city: 0,
            highway: 0,
            tablet: 0,
            hos: 0,
            defensive: 0,
        });
    }

    const goBack = () => {
      if (history.length > 1) history.goBack();
      else history.replace('/Home');
    };

    const totalScore = scores.backing + scores.turning + scores.tablet + scores.roadHabits + scores.hos + scores.highway + scores.city + scores.defensive;
    const formattedDate = now.toJSON().replace('T', ' ').replace('Z', ' ').slice(0, 10);

    const heroTitle = hasTrainee
        ? (showRoadTest ? 'Road Test' : (trainee.traineeCode || 'Trainee'))
        : 'New Report';
    const heroMeta = hasTrainee
        ? (showRoadTest ? 'Complete the road test evaluation' : `Day ${trainee.currentCourseDay}`)
        : (noTrainees ? 'No active trainees' : `${trainees.length} trainee${trainees.length === 1 ? '' : 's'} available`);

    return (
        <IonPage>
            <LoadDetailHeader title="Reports" onBack={goBack} onSheet={onSheet} />

            <IonContent
              fullscreen
              className="design-a-content design-a-content--light"
              scrollEvents={true}
              onIonScroll={onIonScroll}
            >
              <div className={`design-a design-a--detail${mounted ? ' design-a--mounted' : ''}`}>
                <div className="home-hero advance-hero">
                  <div className="advance-hero__eyebrow">Training</div>
                  <div className="advance-hero__title home-display">{heroTitle}</div>
                  <div className="advance-hero__meta">{heroMeta}</div>
                </div>

                <div className="home-sheet advance-sheet">
                  {!hasTrainee ? (
                    noTrainees ? (
                      <div className="scoreboard-empty">
                        <strong>No Trainees</strong>
                        <p>You have no trainees. Claim now.</p>
                        <Link to="/newtrainee" className="home-action home-action--tint">
                          <span className="home-action__icon"><PlusIcon /></span>
                          <span className="home-action__label">New Trainee</span>
                        </Link>
                      </div>
                    ) : (
                      <>
                        <Link to="/newtrainee" className="home-action home-action--tint">
                          <span className="home-action__icon"><PlusIcon /></span>
                          <span className="home-action__label">New Trainee</span>
                          <ChevronRight />
                        </Link>

                        <div className="home-section-label">Select From Current Trainees</div>
                        <div className="training-trainee-list">
                          {trainees.map(t => (
                            <div className="training-trainee-row" key={t.traineeCode}>
                              <button
                                type="button"
                                className="home-action"
                                onClick={() => {
                                  setTraineeData(t);
                                  setShowNewReport(true)
                                }}
                              >
                                <span className="home-action__icon"><SeatedIcon /></span>
                                <span className="home-action__label">
                                  {t.traineeCode}
                                  <span className="training-trainee-row__meta">
                                    {t.currentCourseId === 0 ? 'No Course' : `Course ${t.currentCourseId}`} · Day {t.currentCourseDay}
                                  </span>
                                </span>
                                <ChevronRight />
                              </button>
                              <ReportHistory trainee={t} />
                            </div>
                          ))}
                        </div>
                      </>
                    )
                  ) : showRoadTest ? (
                    <div className="training-roadtest-wrap">
                      <button
                        type="button"
                        className="home-action home-action--tint"
                        onClick={() => setShowRoadTest(false)}
                      >
                        <span className="home-action__icon"><ChevronLeft color="#1a1a1a" size={22} /></span>
                        <span className="home-action__label">Back to report</span>
                      </button>
                      <RoadTest traineeCode={traineeCode} trainerCode={trainee.trainerCode} reportId={setReportId} submittedTest={testSubmitted} hideTest={resetForm} />
                    </div>
                  ) : trainee.currentCourseId !== 0 ? (
                    <>
                      <div className="home-card training-info-card">
                        <div className="training-info-card__label">Activity Prompt</div>
                        <p className="training-info-card__body">No activity prompt for today.</p>
                      </div>

                      <div className="home-card training-info-card">
                        <div className="training-info-card__label">Safety Prompt</div>
                        <p className="training-info-card__body">No safety prompt for today.</p>
                      </div>

                      <button
                        type="button"
                        className="home-action home-action--tint"
                        onClick={() => {
                          setShowCourseModal(true);
                          getCourseInfo();
                        }}
                      >
                        <span className="home-action__icon"><InfoIcon /></span>
                        <span className="home-action__label">Course Info</span>
                        <ChevronRight />
                      </button>

                      <div className="home-card training-roadtest-card">
                        <div className="training-roadtest-card__row">
                          <span className="training-roadtest-card__icon"><WalkthroughIcon /></span>
                          <div className="training-roadtest-card__text">
                            <div className="training-roadtest-card__title">Road Test</div>
                            <div className="training-roadtest-card__status" style={{color: 'var(--home-accent)'}}>Recommended</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="training-secondary-btn"
                          disabled={trainee.currentCourseId === 0}
                          onClick={() => setShowRoadTest(true)}
                        >
                          Road Test Form
                        </button>
                      </div>

                      <div className="home-card training-meta-card">
                        <div className="training-meta-card__row">
                          <span className="training-meta-card__label">Training Date</span>
                          <span className="training-meta-card__value">{formattedDate.slice(5)}-{formattedDate.slice(0, 4)}</span>
                        </div>
                        <div className="training-meta-card__divider" />
                        <div className="training-meta-card__row">
                          <span className="training-meta-card__label">Report Cooldown</span>
                          <span
                            className="training-meta-card__value"
                            style={{color: (reportTimer === 'Not Ready' || trainee.currentCourseId === 0) ? 'var(--home-danger, #B23B34)' : 'var(--home-accent)'}}
                          >
                            {reportTimer}
                          </span>
                        </div>
                      </div>

                      <div className="home-card training-toggle-card">
                        <div className="training-toggle-card__row">
                          <span className="training-toggle-card__icon"><AccidentsIcon /></span>
                          <span className="training-toggle-card__label">Trainee involved in accident?</span>
                          <select
                            className="profile-field__input profile-field__input--select training-toggle-card__select"
                            value={traineeAccident}
                            onChange={e => {
                              setTraineeAccident(e.target.value);
                              if (e.target.value === 'yes') resetScores();
                            }}
                          >
                            <option value="">—</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </div>
                      </div>

                      <div className="home-section-label">Score Each Section</div>
                      <div className="training-sections">
                        {SCORE_SECTIONS.map(section => {
                          const isOpen = openSection === section.key;
                          const score = scores[section.key];
                          const complete = score !== 0;
                          return (
                            <div className="training-section" key={section.key}>
                              <button
                                type="button"
                                className="training-section__header"
                                disabled={traineeAccident === 'yes'}
                                aria-expanded={isOpen}
                                onClick={() => setOpenSection(isOpen ? null : section.key)}
                              >
                                <span className="training-section__icon">{section.icon}</span>
                                <span className="training-section__title">{section.title}</span>
                                <span className="training-section__status" style={{color: complete ? 'var(--home-accent)' : 'var(--home-text-muted)'}}>
                                  {complete ? 'Complete' : 'Not Completed'}
                                </span>
                                <span className={`training-section__chevron${isOpen ? ' is-open' : ''}`} aria-hidden="true">
                                  <ChevronRight />
                                </span>
                              </button>

                              {isOpen ? (
                                <div className="training-section__body">
                                  <p className="training-section__prompt">{section.intro}</p>
                                  <ul className="training-section__bullets">
                                    {section.bullets.map((b, i) => (<li key={i}>{b}</li>))}
                                  </ul>

                                  <div className="training-section__select-row">
                                    <label className="profile-field">
                                      <span className="profile-field__label">Select a Score</span>
                                      <select
                                        className="profile-field__input profile-field__input--select"
                                        disabled={traineeAccident === 'yes'}
                                        value={score}
                                        onChange={e => {
                                          setScores({...scores, [section.key]: Number(e.target.value)});
                                          setOpenSection(null);
                                        }}
                                      >
                                        <option value={0}>—</option>
                                        <option value={1}>1</option>
                                        <option value={2}>2</option>
                                        <option value={3}>3</option>
                                        <option value={4}>4</option>
                                        <option value={5}>5</option>
                                      </select>
                                    </label>
                                  </div>

                                  <ol className="training-section__rubric">
                                    {section.levels.map((lvl, i) => (
                                      <li className="training-section__rubric-item" key={i}>
                                        <span className="training-section__rubric-num">{i + 1}</span>
                                        <span className="training-section__rubric-text">{lvl}</span>
                                      </li>
                                    ))}
                                  </ol>
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>

                      <div className="home-card training-total">
                        <span className="training-total__label">Total Score</span>
                        <span className="training-total__value">
                          {totalScore}<span className="training-total__denominator">/40</span>
                        </span>
                      </div>

                      <div className="home-card training-toggle-card">
                        <div className="training-toggle-card__row">
                          <span className="training-toggle-card__icon"><ReleaseIcon /></span>
                          <span className="training-toggle-card__label">Ready for solo?</span>
                          <select
                            className="profile-field__input profile-field__input--select training-toggle-card__select"
                            disabled={traineeAccident === 'yes'}
                            value={traineeReady}
                            onChange={e => setTraineeReady(e.target.value)}
                          >
                            <option value="">—</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="profile-submit"
                        disabled={reportTimer === 'Not Ready'}
                        onClick={() => setShowModal1(true)}
                      >
                        Submit Report
                      </button>
                    </>
                  ) : (
                    <div className="scoreboard-empty">
                      <strong>No Course Assigned</strong>
                      <p>Assign a course for this trainee below.</p>
                      <button
                        type="button"
                        className="home-action home-action--tint"
                        onClick={() => {
                          setShowCourseModal(true);
                          setChangingCourse(true);
                          getCourseInfo();
                        }}
                      >
                        <span className="home-action__icon"><PlusIcon /></span>
                        <span className="home-action__label">Assign Course</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </IonContent>

            <IonModal isOpen={showNewReport} onDidDismiss={() => setShowNewReport(false)}>
              {traineeData ? (
                <DailyReport traineeData={traineeData} onClose={() => setShowNewReport(false)} />
              ) : null}
            </IonModal>

            <IonModal isOpen={showModal} onDidDismiss={() => setShowModal(false)}>
              <div className="design-a">
                <div className="training-confirm-modal">
                  <div className="training-confirm-modal__title">Thank You</div>
                  <p className="training-confirm-modal__body">Your report has been submitted.</p>
                  <button
                    type="button"
                    className="training-confirm-modal__secondary"
                    onClick={() => { setShowModal(false); history.push('/Home'); }}
                  >
                    Have Feedback?
                  </button>
                  <button
                    type="button"
                    className="profile-submit"
                    onClick={() => {
                      setShowModal(false);
                      setShowModal1(false);
                      history.push('/Home');
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            </IonModal>

            <IonModal isOpen={showModal1} onDidDismiss={() => setShowModal1(false)}>
              <div className="design-a">
                <div className="training-confirm-modal">
                  <div className="training-confirm-modal__title">Confirm?</div>
                  <p className="training-confirm-modal__body">Submit Report for Trainee: {trainee.traineeCode}?</p>
                  <button type="button" className="profile-submit" onClick={() => handleSubmit()}>Confirm</button>
                  <button type="button" className="training-confirm-modal__secondary" onClick={() => setShowModal1(false)}>Cancel</button>
                </div>
              </div>
            </IonModal>

            <IonModal isOpen={showModal2} onDidDismiss={() => setShowModal2(false)}>
              <div className="design-a">
                <div className="training-confirm-modal">
                  <div className="training-confirm-modal__title">Thank You</div>
                  <p className="training-confirm-modal__body">Course change has been submitted.</p>
                  <button
                    type="button"
                    className="profile-submit"
                    onClick={() => { setShowModal2(false); setHasTrainee(false) }}
                  >
                    Done
                  </button>
                </div>
              </div>
            </IonModal>

            <IonModal isOpen={showModal3} onDidDismiss={() => setShowModal3(false)}>
              <div className="design-a">
                <div className="training-confirm-modal">
                  <div className="training-confirm-modal__title">Confirm?</div>
                  <p className="training-confirm-modal__body">Change course for trainee: {trainee.traineeCode}?</p>
                  <button type="button" className="profile-submit" onClick={() => submitCourseChange()}>Confirm</button>
                  <button
                    type="button"
                    className="training-confirm-modal__secondary"
                    onClick={() => { setShowModal3(false); setShowCourseModal(true); setChangingCourse(false) }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </IonModal>

            <IonModal isOpen={showCourseModal} onDidDismiss={() => setShowCourseModal(false)}>
              <div className="design-a">
                <div className="training-modal">
                  <div className="training-modal__header">
                    <span>{changingCourse ? 'Change Course' : 'Course Info'}</span>
                    <button
                      type="button"
                      className="modal-close-btn training-modal__close"
                      onClick={() => setShowCourseModal(false)}
                      aria-label="Close"
                    >
                      <CloseIcon size={18} color="#1a1a1a" />
                    </button>
                  </div>

                  {!changingCourse ? (
                    <div className="training-course-info">
                      <div className="training-course-info__row">
                        <span className="training-course-info__label">Course Name</span>
                        <span className="training-course-info__value">{course.title}</span>
                      </div>
                      <div className="training-course-info__row">
                        <span className="training-course-info__label">Backing Events</span>
                        <span className="training-course-info__value">{course.total_backing_events}</span>
                      </div>
                      <div className="training-course-info__row">
                        <span className="training-course-info__label">Days Required</span>
                        <span className="training-course-info__value">{course.total_days}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="training-course-change">
                      <label className="profile-field">
                        <span className="profile-field__label">Course</span>
                        <select
                          className="profile-field__input profile-field__input--select"
                          value={selectedCourseId ?? ''}
                          onChange={e => setSelectedCourseId(Number(e.target.value))}
                        >
                          <option value="">—</option>
                          {courses.map(c => (<option key={c.id} value={c.id}>{c.title}</option>))}
                        </select>
                      </label>
                      <button
                        type="button"
                        className="profile-submit"
                        onClick={() => { setShowModal3(true); setShowCourseModal(false); setChangingCourse(false) }}
                      >
                        Change
                      </button>
                      <button
                        type="button"
                        className="training-confirm-modal__secondary"
                        onClick={() => setChangingCourse(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </IonModal>
        </IonPage>
    );
};

export default TrainingDay;
